// Ordered routing-step list (spec 01 §4 S4) on the §3.3 `DataTable`, so the
// phone shape (≤768 px: header dropped, each step a labelled card) is the
// primitive's built-in collapse contract rather than a per-screen media query.
// Reorder is available two ways so it works on touch and for keyboard/AT users,
// not only with a mouse: native HTML5 drag AND explicit up/down buttons — both
// renumber by 10s in the parent (model.renumberSteps).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../../components/data-table";
import { StatusChip } from "../../../../../components/status-chip";
import { instructionsSummary, workCenterLabel, type StepDraft, type WorkCenter } from "./model";

function StepFlags({ step }: { step: StepDraft }) {
  const { t } = useTranslation();
  const flags: string[] = [];
  if (step.is_outsourced) flags.push(t("catalogue.routing.flags.outsourced"));
  if (step.requires_measured_capture) flags.push(t("catalogue.routing.flags.measured"));
  if (step.requires_serial_capture) flags.push(t("catalogue.routing.flags.serial"));
  if (!step.backflush) flags.push(t("catalogue.routing.flags.manualIssue"));
  if (step.can_overlap) flags.push(t("catalogue.routing.flags.overlap"));
  if (step.qc_control_point_id !== null && step.qc_control_point_id.length > 0)
    flags.push(t("catalogue.routing.flags.qc"));
  if (step.alternate_work_center_ids.length > 0)
    flags.push(
      t("catalogue.routing.flags.alternates", { n: step.alternate_work_center_ids.length }),
    );
  const instr = instructionsSummary(step.instructions);
  if (instr.text + instr.image > 0)
    flags.push(t("catalogue.routing.flags.instructions", { n: instr.text + instr.image }));
  if (flags.length === 0) return null;
  return (
    <span className="rte-flags">
      {flags.map((f) => (
        <StatusChip variant="neutral" dot={false} key={f}>
          {f}
        </StatusChip>
      ))}
    </span>
  );
}

export function StepList({
  steps,
  workCenterById,
  readOnly,
  onEdit,
  onMove,
  onRemove,
  onReorder,
}: {
  steps: readonly StepDraft[];
  workCenterById: ReadonlyMap<string, WorkCenter>;
  readOnly: boolean;
  onEdit: (index: number) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onRemove: (index: number) => void;
  onReorder: (from: number, to: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const lang = i18n.language;

  function wcLabel(step: StepDraft): string {
    const wc = workCenterById.get(step.work_center_id);
    return wc !== undefined ? `${wc.code} · ${workCenterLabel(wc, lang)}` : step.work_center_id;
  }

  function runSummary(step: StepDraft): string {
    const unit =
      step.run_basis === "per_batch"
        ? t("catalogue.routing.runSummary.perBatch", { minutes: step.run_minutes })
        : t("catalogue.routing.runSummary.perUnit", { minutes: step.run_minutes });
    return t("catalogue.routing.runSummary.line", { setup: step.setup_minutes, run: unit });
  }

  const indexOf = (step: StepDraft) => steps.indexOf(step);

  const columns: DataTableColumn<StepDraft>[] = [
    {
      key: "step_no",
      header: t("catalogue.routing.columns.step"),
      id: true,
      render: (step) => step.step_no,
    },
    {
      key: "operation",
      header: t("catalogue.routing.columns.operation"),
      render: (step) => (
        <>
          <span className="rte-step-name">
            {step.name.trim().length > 0 ? (
              step.name
            ) : (
              <span className="rte-muted">{t("catalogue.routing.unnamedStep")}</span>
            )}
          </span>
          <StepFlags step={step} />
        </>
      ),
    },
    {
      key: "work_center",
      header: t("catalogue.routing.columns.workCenter"),
      render: (step) => wcLabel(step),
    },
    {
      key: "times",
      header: t("catalogue.routing.columns.times"),
      render: (step) => runSummary(step),
    },
    {
      key: "actions",
      header: t("catalogue.routing.columns.actions"),
      actions: true,
      render: (step) => {
        const index = indexOf(step);
        return (
          <span className="rte-row-actions">
            <Button
              size="sm"
              aria-label={t("catalogue.routing.actions.moveUp", { n: step.step_no })}
              disabled={readOnly || index === 0}
              onClick={() => {
                onMove(index, -1);
              }}
            >
              ↑
            </Button>
            <Button
              size="sm"
              aria-label={t("catalogue.routing.actions.moveDown", { n: step.step_no })}
              disabled={readOnly || index === steps.length - 1}
              onClick={() => {
                onMove(index, 1);
              }}
            >
              ↓
            </Button>
            {/* The TEXTUAL row action is an accent link, the same register S5's
                actions column uses — a grey `ds-btn-sm` here and an accent link
                one screen over is the "row actions rendered three ways"
                inconsistency. The ↑ ↓ ✕ glyphs stay BUTTONS: they are direct
                controls that act on the row in place, not navigations. */}
            <button
              type="button"
              className="ds-lnk"
              onClick={() => {
                onEdit(index);
              }}
            >
              {readOnly ? t("catalogue.routing.actions.view") : t("catalogue.routing.actions.edit")}
            </button>
            {/* Remove is an OFFER of a destructive act, and the register
                reserves red for the confirmation of one, not for a column of
                them (§1 rule 3). */}
            {!readOnly ? (
              <Button
                size="sm"
                aria-label={t("catalogue.routing.actions.remove", { n: step.step_no })}
                onClick={() => {
                  onRemove(index);
                }}
              >
                ✕
              </Button>
            ) : null}
          </span>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={steps}
      rowKey={(step) => step._key}
      rowTestId="step-row"
      caption={t("catalogue.routing.stepsTitle")}
      data-testid="step-list"
      rowClassName={(step) => (dragIndex === indexOf(step) ? "rte-dragging" : undefined)}
      rowProps={(step, index) => ({
        "data-step-no": step.step_no,
        draggable: !readOnly,
        onDragStart: () => {
          if (!readOnly) setDragIndex(index);
        },
        onDragOver: (e) => {
          if (!readOnly && dragIndex !== null) e.preventDefault();
        },
        onDrop: (e) => {
          if (readOnly || dragIndex === null) return;
          e.preventDefault();
          if (dragIndex !== index) onReorder(dragIndex, index);
          setDragIndex(null);
        },
        onDragEnd: () => {
          setDragIndex(null);
        },
      })}
      empty={null}
    />
  );
}
